import * as React from 'react'
import { useState } from 'react'

import {
  Box,
  Button,
  CounterBadge,
  Icon,
  type SortDirection,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import Select from 'modules/core/components/Select'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

interface Props {
  /**
   * Use a more minimal representation for a modern look.
   */
  dense?: boolean
  label: string
  attachment?: Attachment
  onApplyGroupFilter: () => void
  onChangeGroupFilter: (order: string) => void
  onChangeGroupOrder: (order: SortDirection) => void
  groupFilterFields: { label: string; value: string }[]
  groupOrderFields: { label: string; value: string }[]
  countGroupFilters: number
  savedDefaultGroupingFilters: string
}

const GroupFilters = ({
  dense,
  label,
  attachment = 'top center',
  onApplyGroupFilter,
  onChangeGroupFilter,
  onChangeGroupOrder,
  groupFilterFields,
  groupOrderFields,
  countGroupFilters,
  savedDefaultGroupingFilters,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [defaultOption, setDefaultOption] = useState<string>('')
  const [defaultOrderOption, setDefaultOrderOption] = useState(groupOrderFields[0]?.value ?? '')
  const noFilter = ''
  const defaultLocalGroupingFilters: [groupfilter: string, order: string] = ['', 'asc']
  const [localDefaultGroupingFilters, setlocalDefaultGroupingFilters] = useLocalStorage<
    [string, string]
  >(savedDefaultGroupingFilters, defaultLocalGroupingFilters)
  useUnsafeEffect(() => {
    if (localDefaultGroupingFilters && localDefaultGroupingFilters[0] !== undefined) {
      setDefaultOption(localDefaultGroupingFilters[0])
      setDefaultOrderOption(localDefaultGroupingFilters[1])
    } else {
      setDefaultOrderOption(groupOrderFields[0]?.value ?? '')
    }
  }, [localDefaultGroupingFilters, groupOrderFields])

  const putDefaultOption = (value: string) => {
    groupFilterFields.forEach((group) => {
      if (group.value !== value) return

      setDefaultOption(value)

      if (localDefaultGroupingFilters)
        setlocalDefaultGroupingFilters([value, localDefaultGroupingFilters[1]])
    })
  }

  const putDefaultOrderOption = (value: string) => {
    groupOrderFields.forEach((order) => {
      if (order.value !== value) return

      setDefaultOrderOption(value)

      if (localDefaultGroupingFilters)
        setlocalDefaultGroupingFilters([localDefaultGroupingFilters[0], value])
    })
  }

  const onChangeGroupFilterHandler = (filter: string) => {
    if (filter === noFilter) {
      toggle(false)()
      setlocalDefaultGroupingFilters(defaultLocalGroupingFilters)
    }

    putDefaultOption(filter)
    onChangeGroupFilter(filter)
  }

  const onChangeGroupOrderHandler = (order: string) => {
    putDefaultOrderOption(order)
    onChangeGroupOrder(order as SortDirection)
  }

  const onCancelHandler = () => {
    toggle(false)()
  }

  const onApplyGroupFilterHandler = () => {
    toggle(false)()
    onApplyGroupFilter()
  }

  const icon = open ? (dense ? 'chevron-up' : 'caret-up') : dense ? 'chevron-down' : 'caret-down'

  return (
    <Popover open={open} toggle={toggle} attachment={attachment} size="auto">
      <Box
        onClick={toggle(true)}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        background="grey000White"
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        cursor="pointer"
        gap="s6"
        {...(dense
          ? {
              paddingX: 's12',
              paddingY: 's6',
            }
          : {
              padding: 's8',
              width: 's140',
            })}
      >
        <Box flexDirection="row" gap="s6">
          <CounterBadge count={countGroupFilters} noCountIcon={dense ? undefined : 'group'} />
          <Text size="300" color="grey1000">
            {label}
          </Text>
        </Box>
        <Icon.Small icon={icon} />
      </Box>

      <Box>
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Box width="s80" flexDirection="row" justifyContent="flexEnd">
            <Text.Normal weight="medium">{i18n.t('filters.group_filters.group_by')}</Text.Normal>
          </Box>
          <Box>
            <Select
              options={groupFilterFields.map((fieldOption) => ({
                label: fieldOption.label,
                value: fieldOption.value,
              }))}
              none={i18n.t('filters.filter.none')}
              onChange={onChangeGroupFilterHandler}
              value={defaultOption}
            />
          </Box>

          <Box>
            <Text.Normal weight="medium">
              {i18n.t('filters.group_filters.ordering.order')}
            </Text.Normal>
          </Box>
          <Box paddingLeft="s12" paddingTop="s24" paddingRight="s24" paddingBottom="s24">
            <Select
              options={groupOrderFields}
              none={i18n.t('filters.filter.none')}
              onChange={onChangeGroupOrderHandler}
              value={defaultOrderOption}
            />
          </Box>
        </Box>

        <Box
          border={{
            top: {
              width: 's1',
              style: 'solid',
              color: 'darker160',
            },
          }}
          borderRadius={{
            bottomLeft: 'abs016',
            bottomRight: 'abs016',
          }}
          background="grey100"
          paddingY="s12"
          paddingX="s32"
          justifyContent="flexEnd"
          flexDirection="row"
          flexWrap="wrap"
          gap="s8"
        >
          <>
            <Button onClick={onCancelHandler} type="secondary">
              {i18n.t('filters.group_filters.commands.cancel')}
            </Button>
            <Button
              onClick={onApplyGroupFilterHandler}
              type="primary"
              disabled={defaultOption === '' || groupFilterFields.length < 1}
            >
              {i18n.t('filters.group_filters.commands.apply')}
            </Button>
          </>
        </Box>
      </Box>
    </Popover>
  )
}

export default observer(GroupFilters)
